import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SchoolIcon from "assets/images/school.png";
import SoftTypography from "components/SoftTypography";
import { figmaTokens } from "assets/theme/designTokens";

const DEFAULT_CENTER = [12.35, -1.52];
const DEFAULT_ZOOM = 6;

function EstablishmentMapPicker({ latitude, longitude, onLocationChange, active, hint }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, { zoomControl: true }).setView(
      hasCoords ? [lat, lng] : DEFAULT_CENTER,
      hasCoords ? 14 : DEFAULT_ZOOM
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    map.on("click", (event) => {
      const { lat: clickLat, lng: clickLng } = event.latlng;
      onLocationChange?.({
        latitude: clickLat.toFixed(6),
        longitude: clickLng.toFixed(6),
      });
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (hasCoords) {
      const icon = L.icon({
        iconUrl: SchoolIcon,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });
      markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
      map.setView([lat, lng], Math.max(map.getZoom(), 13), { animate: false });
    }
  }, [lat, lng, hasCoords]);

  useEffect(() => {
    if (!active || !mapRef.current) return;
    const timer = window.setTimeout(() => {
      mapRef.current?.invalidateSize();
      if (hasCoords) {
        mapRef.current?.setView([lat, lng], Math.max(mapRef.current.getZoom(), 13));
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [active, hasCoords, lat, lng]);

  return (
    <>
      {hint ? (
        <SoftTypography variant="caption" sx={{ display: "block", mb: 1, color: figmaTokens.textMuted }}>
          {hint}
        </SoftTypography>
      ) : null}
      <div
        ref={containerRef}
        style={{
          height: 280,
          width: "100%",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${figmaTokens.borderSubtle}`,
        }}
      />
    </>
  );
}

EstablishmentMapPicker.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onLocationChange: PropTypes.func,
  active: PropTypes.bool,
  hint: PropTypes.string,
};

EstablishmentMapPicker.defaultProps = {
  latitude: "",
  longitude: "",
  onLocationChange: () => {},
  active: true,
  hint: "",
};

export default EstablishmentMapPicker;
